// Copyright (c) Ping. All rights reserved.

import React, { useCallback } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import type { LeaderboardColumn, LeaderboardSort } from './types';

export interface LeaderboardColumnHeaderProps {
  readonly column: LeaderboardColumn;
  readonly sort: LeaderboardSort | null;
  readonly onPress: (column: LeaderboardColumn) => void;
}

const COLUMN_WIDTH = 140;

/** Sortable header for one column. */
export const LeaderboardColumnHeader = React.memo<LeaderboardColumnHeaderProps>(
  ({ column, sort, onPress }) => {
    const scheme = useColorScheme();
    const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');
    const active = sort !== null && sort.columnId === column.id;
    const indicator = active ? (sort?.direction === 'ascending' ? '▲' : '▼') : '';

    const handlePress = useCallback(() => onPress(column), [column, onPress]);

    return (
      <Pressable
        onPress={handlePress}
        accessibilityRole="button"
        accessibilityLabel={column.title}
        accessibilityState={{ selected: active }}
        style={({ pressed }) => [
          styles.header,
          {
            backgroundColor: pressed
              ? theme.colors.surface.tertiary
              : theme.colors.surface.secondary,
            borderRightColor: theme.colors.border.secondary,
          },
        ]}
      >
        <Text
          style={[
            styles.title,
            {
              color: active ? theme.colors.accent.indigo : theme.colors.text.secondary,
            },
          ]}
          numberOfLines={1}
        >
          {column.title}
          {indicator !== '' ? ` ${indicator}` : ''}
        </Text>
      </Pressable>
    );
  },
);

LeaderboardColumnHeader.displayName = 'LeaderboardColumnHeader';

export const LEADERBOARD_COLUMN_WIDTH = COLUMN_WIDTH;

const styles = StyleSheet.create({
  header: {
    width: COLUMN_WIDTH,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRightWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  title: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
  } satisfies TextStyle,
});
